import {Component, Injector} from '@angular/core';
import { AuthService, ShellService } from '@edifecs/fp-ui-shell';
import {AppConstants} from '@app/AppConstants';
import {ColorUtil, ufn} from '@edifecs/fp-ui-lib';
import {AppConfigService} from '@app/core/services/AppConfigService';

/**
 * UI about the app. Primarily for CMFA mode.
 */
@Component({
    selector: 'app-info',
    templateUrl: './AppInfoComponent.html',
    styleUrls: ['./AppInfoComponent.css'],
    standalone: false
})
export class AppInfoComponent {
    disclaimers: any[];
    authInfo = this.authSvc.getUserInfo();

    /**
     * Constructor
     */
    constructor(private readonly shellSvc: ShellService, private readonly appConfig: AppConfigService,
                private readonly authSvc: AuthService) {
        this.disclaimers = this.getDisclaimers();
    }

    /**
     * @internal
     * Handle close request
     */
    onClose(): void {
        this.shellSvc.hideModalWindow();
    }

    /**
     * @internal
     * Branding configuration
     */
    get branding(): any {
        return this.appConfig?.getLocalSettings()?.brandingData;
    }

    /**
     * @internal
     * Logo image
     */
    get logoImage(): string {
        if (AppConstants.CMFA_MODE) {
            const brandingImg = this.branding?.companyData?.logoImage;
            return ufn.isText(brandingImg) ? brandingImg : AppConstants.NAV_LOGO_CMFA;
        }
        return AppConstants.NAV_LOGO_FPP;
    }

    /**
     * @internal
     * Business name
     */
    get businessName(): string {
        if (AppConstants.CMFA_MODE) {
            const brandingName = this.branding?.companyData.businessName;
            return ufn.isText(brandingName) ? brandingName : AppConstants.APP_TITLE_CMFA;
        }

        return AppConstants.APP_TITLE_FPP;
    }

    /**
     * @internal
     * Business name css color
     */
    get businessNameColor(): string {
        return ColorUtil.asHtmlStyleColor(this.branding?.['styles.business-name-color']) ?? '#616d7a';
    }

    /**
     * @internal
     * Privacy policy URL
     */
    get privacyPolicyUrl(): string | undefined {
        if (AppConstants.CMFA_MODE) {
            const url = this.branding?.['privacy-policy-url'];
            return ufn.isText(url) ? url : undefined;
        }
        return undefined;
    }

    /**
     * @internal
     * Terms of Service URL
     */
    get termsOfServiceUrl(): string | undefined {
        if (AppConstants.CMFA_MODE) {
            const url = this.branding?.['terms-of-service-url'];
            return ufn.isText(url) ? url : undefined;
        }
        return undefined;
    }

    /**
     * @internal
     * Plan name
     */
    get planName(): string | undefined {
        if (AppConstants.CMFA_MODE) {
            return this.branding?.['plan-name'];
        }
        return undefined;
    }

    /**
     * @internal
     * Copyright Notice
     */
    get copyright(): string {
        return `Copyright © ${new Date().getFullYear()} ${ufn.isText(
            this.planName) ? this.planName : 'Edifecs Inc'}. All rights reserved.`;
    }

    /**
     * Open app info in side window.
     * @param shellSvc Shell service
     * @param injector Injector
     */
    public static show(shellSvc: ShellService, injector: Injector): void {
        shellSvc.showModalWindow({
            align: 'left',
            alignOffset: 188,
            content: {
                injector,
                component: AppInfoComponent
            }
        });
    }

    /**
     * get disclaimers
     */
    getDisclaimers(): any[] {
        const entries = this.branding?.disclaimer?.items;
        const disclaimerList: any[] = [];
        if (entries) {
            entries.map((entry: any) => {
                const originalText = entry.text;
                if (originalText) {
                    const updatedText = originalText.replace(/\${([^}]+)}/gi, (_match: any, key: string) => {
                        const param = entry.params?.[key];
                        const textWithLink =
                            `<a class="link-text" href="${param?.url}" target="_blank">${param?.caption}</a>`;
                        return param ? textWithLink : `\${${key}}`;
                    });
                    disclaimerList.push(updatedText);
                }
            });
        }
        return disclaimerList;
    }
}
